import { CostLineType } from '@/features/dashboard/bookings/types/BookingBillDto.ts';
import {
  ChangePreviewDto,
  TargetCostLineDto,
} from '@/features/dashboard/bookingChanges/types/dto/ChangePreviewDto.ts';
import {
  BOOKING_WIDE_KEY,
  ChangePreview,
  costRowKey,
} from '@/features/dashboard/bookingChanges/types/ChangePreview.ts';
import { ChangeItemDto } from '@/features/dashboard/bookingChanges/types/dto/ChangeItemDto.ts';
import { bookingWideDiscountDescriptions } from '@/features/dashboard/bookingChanges/utils/draftItems.ts';

/**
 * Indeks podglądu paczki: trzy słowniki nad jedną odpowiedzią.
 *
 * Nic tu się nie liczy — kwoty przechodzą nietknięte. Zmienia się wyłącznie
 * sposób adresowania: lista wpisów staje się słownikiem, żeby wiersz rachunku
 * odnalazł swoją deltę bez przeszukiwania tablicy przy każdym renderze.
 */

/**
 * Klucz wiersza dla pozycji księgi. Liczony z obu stron wpisu tak samo, bo
 * `current` i `target` opisują tę samą pozycję rachunku w dwóch stanach.
 */
const rowKeyOf = (line: TargetCostLineDto): string => {
  switch (line.type) {
    case CostLineType.OPTION:
      return costRowKey(line.participantId, line.type, line.tourOptionId);
    case CostLineType.DISCOUNT:
      return costRowKey(line.participantId, line.type, line.discountId);
    case CostLineType.MANUAL:
      // Pozycja ręczna naturalnego klucza nie ma — backend przenosi ją 1:1,
      // a ekran musi ją czymś rozpoznać, więc zostaje opis nadany przez
      // operatora. Dwie pozycje o tym samym opisie u tej samej osoby są
      // nierozróżnialne i po obu stronach.
      return costRowKey(line.participantId, line.type, line.description);
    default:
      return costRowKey(line.participantId, line.type);
  }
};

/**
 * Klucze, pod którymi wiersz rachunku szuka dopisanej pozycji — zwykle jeden.
 *
 * Zniżka założona **na całą rezerwację** jest wyjątkiem: backend rozdziela ją
 * na uczestników proporcjonalnie do ich udziału, więc w podglądzie nie ma ani
 * jednej pozycji bez osoby, a wiersz stoi w sekcji rezerwacji i po własnym
 * kluczu nie znalazłby nic. Dokładamy mu więc klucz zbiorczy — ale tylko
 * udziałom, czyli liniom uczestników o opisie zniżki całej rezerwacji ze
 * szkicu. Własna pozycja uczestnika o tym samym opisie co dopłata rezerwacji
 * (której backend nie rozkłada) doliczyłaby się inaczej do cudzego wiersza.
 */
const rowKeysOf = (
  line: TargetCostLineDto,
  discountDescriptions: Set<string>,
): string[] => {
  const key = rowKeyOf(line);
  if (
    line.type !== CostLineType.MANUAL ||
    line.participantId === null ||
    !discountDescriptions.has(line.description)
  ) {
    return [key];
  }
  return [key, costRowKey(null, line.type, line.description)];
};

export const changePreviewMapper = {
  /** `items` — pozycje szkicu; po nich rozpoznaje udziały zniżki całej rezerwacji. */
  toChangePreview(
    dto: ChangePreviewDto,
    items: ChangeItemDto[],
  ): ChangePreview {
    const discountDescriptions = new Set(
      bookingWideDiscountDescriptions(items),
    );
    const preview: ChangePreview = {
      total: dto.delta,
      byParticipant: {},
      changedLines: {},
      addedRows: {},
    };

    dto.deltaByParticipant.forEach((entry) => {
      preview.byParticipant[entry.participantId ?? BOOKING_WIDE_KEY] =
        entry.totals;
    });

    dto.deltaByLine.forEach((entry) => {
      if (entry.current) {
        preview.changedLines[entry.current.id] = {
          delta: entry.delta,
          removed: entry.target === null,
        };
        return;
      }
      if (entry.target) {
        rowKeysOf(entry.target, discountDescriptions).forEach((key) => {
          preview.addedRows[key] = [
            ...(preview.addedRows[key] ?? []),
            entry.delta,
          ];
        });
      }
    });

    return preview;
  },
};
